import { router } from '@inertiajs/react'
import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Calendar, Check, AlertCircle } from 'lucide-react'

interface CalendarEvent {
  id: string
  title: string
  due_date: string
  source: 'system' | 'user' | 'admin'
  completed: boolean
  scs_type: string
  scs_id: string
  scs_label: string
  account_name: string | null
}

type Props = {
  events: CalendarEvent[]
  filters: { scs_type: string | null }
}

const SCS_DOT_COLORS: Record<string, string> = {
  formation: 'bg-blue-500',
  tax_registration: 'bg-purple-500',
  compliance_filing: 'bg-orange-500',
  nominee_shareholder: 'bg-emerald-500',
  nominee_director: 'bg-teal-500',
  business_task: 'bg-rose-500',
  registered_agent: 'bg-cyan-500',
  registered_address: 'bg-yellow-500',
  license: 'bg-lime-500',
  standalone: 'bg-indigo-500',
}

const SCS_LABELS: Record<string, string> = {
  formation: 'Formation',
  tax_registration: 'Tax Registration',
  compliance_filing: 'Compliance Filing',
  nominee_shareholder: 'Nominee Shareholder',
  nominee_director: 'Nominee Director',
  business_task: 'Business Task',
  registered_agent: 'Registered Agent',
  registered_address: 'Registered Address',
  license: 'License',
  standalone: 'Custom',
}

const SCS_PATH_PREFIX: Record<string, string> = {
  formation: '/backoffice/formations',
  tax_registration: '/backoffice/tax_registrations',
  compliance_filing: '/backoffice/compliance_filings',
  nominee_shareholder: '/backoffice/nominee_shareholders',
  nominee_director: '/backoffice/nominee_directors',
  business_task: '/backoffice/business_tasks',
  registered_agent: '/backoffice/registered_agents',
  registered_address: '/backoffice/registered_addresses',
  license: '/backoffice/licenses',
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00')
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function relativeDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00')
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const diff = Math.round((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))

  if (diff < 0) return `${Math.abs(diff)}d overdue`
  if (diff === 0) return 'Today'
  if (diff === 1) return 'Tomorrow'
  return `in ${diff}d`
}

function groupEvents(events: CalendarEvent[]) {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const endOfWeek = new Date(today)
  endOfWeek.setDate(today.getDate() + (7 - today.getDay()))

  const endOfNextWeek = new Date(endOfWeek)
  endOfNextWeek.setDate(endOfWeek.getDate() + 7)

  const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0)

  const groups: Record<string, CalendarEvent[]> = {
    overdue: [],
    this_week: [],
    next_week: [],
    this_month: [],
    later: [],
  }

  events.forEach(event => {
    const date = new Date(event.due_date + 'T00:00:00')
    if (date < today) groups.overdue.push(event)
    else if (date <= endOfWeek) groups.this_week.push(event)
    else if (date <= endOfNextWeek) groups.next_week.push(event)
    else if (date <= endOfMonth) groups.this_month.push(event)
    else groups.later.push(event)
  })

  return groups
}

function EventRow({ event }: { event: CalendarEvent }) {
  const isOverdue = new Date(event.due_date + 'T00:00:00') < new Date(new Date().toDateString())
  const pathPrefix = SCS_PATH_PREFIX[event.scs_type]
  const href = pathPrefix ? `${pathPrefix}/${event.scs_id}` : undefined

  return (
    <div className="flex items-center justify-between px-4 py-3 border-t first:border-t-0">
      <div className="flex items-start gap-3 min-w-0">
        <div className={`w-2 h-2 rounded-full shrink-0 mt-1.5 ${SCS_DOT_COLORS[event.scs_type] ?? 'bg-gray-400'}`} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {href ? (
              <a href={href} className="text-sm font-medium text-foreground truncate hover:underline">
                {event.title}
              </a>
            ) : (
              <span className="text-sm font-medium text-foreground truncate">{event.title}</span>
            )}
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-0.5">
            <span className="flex items-center gap-1">
              <span className={`inline-block w-1.5 h-1.5 rounded-full ${SCS_DOT_COLORS[event.scs_type] ?? 'bg-gray-400'}`} />
              {SCS_LABELS[event.scs_type] ?? event.scs_type}
            </span>
            <span>&middot;</span>
            <span>{event.scs_label}</span>
            {event.account_name && (
              <>
                <span>&middot;</span>
                <span>{event.account_name}</span>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0 ml-4">
        <div className="text-right">
          <div className="text-sm text-foreground">{formatDate(event.due_date)}</div>
          <div className={`text-xs ${isOverdue ? 'text-red-600 font-medium' : 'text-muted-foreground'}`}>
            {isOverdue && <AlertCircle className="w-3 h-3 inline mr-0.5 -mt-0.5" />}
            {relativeDate(event.due_date)}
          </div>
        </div>
        {event.completed && (
          <Badge variant="secondary" className="text-xs">
            <Check className="w-3 h-3 mr-1" />
            Done
          </Badge>
        )}
      </div>
    </div>
  )
}

export default function BackofficeCalendarShow({ events, filters }: Props) {
  const groups = groupEvents(events)

  const groupLabels: Record<string, string> = {
    overdue: 'Overdue',
    this_week: 'This Week',
    next_week: 'Next Week',
    this_month: 'This Month',
    later: 'Later',
  }

  return (
    <BackofficeLayout title="Calendar" breadcrumbs={[
      { label: 'Backoffice', href: '/backoffice/dashboard' },
      { label: 'Calendar' },
    ]}>
      <div>
        <div className="mb-8">
          <h1 className="text-xl font-semibold tracking-tight">Calendar</h1>
          <p className="text-sm text-muted-foreground">Upcoming events and deadlines across all accounts and services</p>
          <div className="flex items-center gap-2 mt-3">
            <Select
              value={filters.scs_type ?? 'all'}
              onValueChange={(value) => {
                if (value === 'all') {
                  router.get('/backoffice/calendar')
                } else {
                  router.get('/backoffice/calendar', { scs_type: value })
                }
              }}
            >
              <SelectTrigger className="w-52 h-9 text-sm">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Events</SelectItem>
                {Object.entries(SCS_LABELS).map(([type, label]) => (
                  <SelectItem key={type} value={type}>
                    <span className="flex items-center gap-2">
                      <span className={`inline-block w-2 h-2 rounded-full ${SCS_DOT_COLORS[type] ?? 'bg-gray-400'}`} />
                      {label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {events.length === 0 ? (
          <div className="border rounded-lg py-16 text-center">
            <Calendar className="w-6 h-6 mx-auto mb-3 text-muted-foreground/30" />
            <h3 className="text-sm font-medium text-foreground mb-1">No upcoming events</h3>
            <p className="text-sm text-muted-foreground">Events will appear here as accounts use services</p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupLabels).map(([key, label]) => {
              const groupEventsArr = groups[key]
              if (!groupEventsArr || groupEventsArr.length === 0) return null
              return (
                <div key={key}>
                  <h2 className={`text-xs font-semibold uppercase tracking-wider mb-2 ${key === 'overdue' ? 'text-red-600' : 'text-muted-foreground'}`}>
                    {label} ({groupEventsArr.length})
                  </h2>
                  <div className="border rounded-lg divide-y">
                    {groupEventsArr.map(event => (
                      <EventRow key={`${event.scs_type}-${event.id}`} event={event} />
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </BackofficeLayout>
  )
}
